import React, { Component } from 'react';
import * as PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  InputAdornment,
  TableCell,
  Tooltip,
} from '@mui/material';
import { Check, Close, Delete } from 'mdi-material-ui';
import Formsy from 'formsy-react';

import { TextField } from '../utils';
import { deleteThreshold, saveThreshold } from '../../actions/fdd/FDDThresholdActions';
import { addGlobalMessage } from '../../actions/SnackbarActions';
import EditableTableRow from '../utils/EditableTableRow';
import { formatRoundedCurrency } from '../../utils/currency-utils';
import { addMinValidationRule } from '../../utils/validation-rules';
import { RegionAutocompleteField } from '../commons/autocompleteFields';

class ThresholdRow extends Component {
  static errorMessages = {
    region: {
      isDefaultRequiredValue: 'Veuillez sélectionner une région',
      isExisty: 'Veuillez sélectionner une région',
    },
    amount: {
      isExisty: 'Veuillez saisir un montant',
      isInt: 'Veuillez saisir un montant entier',
      min: 'Veuillez saisir un montant supérieur à 0',
    },
  };

  static propTypes = {
    threshold: PropTypes.object.isRequired,
    editing: PropTypes.bool.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      threshold: {},
      editing: this.props.editing || false,
      deleting: false,
    };

    this.form = React.createRef();
    addMinValidationRule();
  }

  onEditClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const { threshold } = this.props;
    this.setState(({ editing }) => (editing ? null : { editing: true, threshold }));
  };

  onChange = (fieldName) => (value) => {
    this.setState(({ threshold }) => ({
      threshold: {
        ...threshold,
        [fieldName]: value,
      },
    }));
  };

  onKeyDown = (e) => {
    if (e.keyCode === 27) {
      // Case escape is pressed
      this.onCancelEdit(e);
    }
  };

  onCancelEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const { dispatch, threshold } = this.props;

    if (!threshold.id) {
      dispatch(deleteThreshold(threshold));
      return;
    }

    this.setState({
      threshold: {},
      editing: false,
    });

    dispatch(addGlobalMessage('Modifications annulées'));
  };

  onSubmitClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.form.current.submit();
  };

  onSave = () => {
    const { dispatch } = this.props;
    const { threshold } = this.state;

    this.setState({ editing: false });

    dispatch(saveThreshold(threshold));
    dispatch(addGlobalMessage('Modifications sauvegardées'));
  };

  onAskDeleteConfirmation = (e) => {
    e.preventDefault();
    e.stopPropagation();

    this.setState({
      deleting: true,
    });
  };

  onCancelDelete() {
    this.setState({
      deleting: false,
    });
  }

  onDelete() {
    const { dispatch, threshold } = this.props;
    dispatch(deleteThreshold(threshold));
    dispatch(addGlobalMessage('Seuil supprimé'));
  }

  renderForm = (props) => <Formsy ref={this.form} noValidate onValidSubmit={this.onSave} {...props} />;

  render() {
    const { threshold, editing, deleting } = this.state;
    const { threshold: thresholdProp } = this.props;
    const creating = !thresholdProp.id;
    const isDefault = !creating && !thresholdProp.region;

    return (
      <EditableTableRow
        component={this.renderForm}
        editable={true}
        editing={editing}
        onEdit={(e) => this.onEditClick(e)}
        actions={
          <>
            <Tooltip title="Enregistrer le seuil">
              <IconButton type="submit" onClick={this.onSubmitClick} size="large">
                <Check />
              </IconButton>
            </Tooltip>
            <Tooltip title="Annuler les changements">
              <IconButton onClick={this.onCancelEdit} size="large">
                <Close />
              </IconButton>
            </Tooltip>
            {!creating && !isDefault && (
              <Tooltip title="Supprimer le seuil">
                <IconButton onClick={this.onAskDeleteConfirmation} size="large">
                  <Delete />
                </IconButton>
              </Tooltip>
            )}
          </>
        }
        TableCellProps={{ component: 'div' }}
      >
        <TableCell component="div">
          {editing && !isDefault ? (
            <RegionAutocompleteField
              name="region"
              value={threshold.region && threshold.region.id}
              onChange={(id, region) => this.onChange('region')(region)}
              validations="isExisty"
              validationErrors={ThresholdRow.errorMessages.region}
            />
          ) : (
            <span>{thresholdProp.region ? thresholdProp.region.name : <i>Par défaut</i>}</span>
          )}
        </TableCell>
        <TableCell component="div">
          {editing ? (
            <TextField
              type="number"
              name="amount"
              value={threshold.amount || null}
              onChange={(e) => this.onChange('amount')(e.target.value)}
              fullWidth
              onKeyDown={this.onKeyDown}
              min="1"
              validations="isExisty,isInt,min:1"
              validationErrors={ThresholdRow.errorMessages.amount}
              InputProps={{
                endAdornment: <InputAdornment position="end">€</InputAdornment>,
              }}
            />
          ) : (
            <span>{formatRoundedCurrency(thresholdProp.amount)}</span>
          )}
        </TableCell>

        {editing && (
          <Dialog open={deleting} onClose={() => this.onCancelDelete()}>
            <DialogContent>
              <DialogContentText>Êtes-vous sûr de vouloir supprimer le seuil ?</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => this.onCancelDelete()}>Annuler</Button>
              <Button color="secondary" onClick={() => this.onDelete()}>
                Supprimer
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </EditableTableRow>
    );
  }
}

export default connect()(ThresholdRow);
